import { useState, useEffect, useRef, Component } from 'react';
import { useParams } from 'react-router-dom';
import { Circle, Warning, SpeakerHigh, SpeakerSlash, ArrowsOut, Play, Pause } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Dynamic import for flv.js
let flvjs = null;

// Error Boundary to catch React errors
class VideoErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Video Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center p-6 max-w-md">
            <Warning size={64} className="text-red-500" />
            <h2 className="text-white text-xl font-semibold">Error de video</h2>
            <p className="text-gray-300">
              Ocurrió un error al cargar el reproductor. Esto puede deberse a que el dispositivo 
              no tiene video activo o hay un problema de conexión.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * LiveVideoPage - Standalone page for direct FLV video streaming
 * 
 * URL: /live/{imei}?channel=0
 * 
 * Opens directly from 4sat.cl/GPSWOX with auto-play
 */
const LiveVideoPage = () => {
  const { imei } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const channel = parseInt(searchParams.get('channel') || '0', 10);
  
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  
  const [streamUrl, setStreamUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [flvLoaded, setFlvLoaded] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [allowAudioControl, setAllowAudioControl] = useState(false);
  const [allowedChannels, setAllowedChannels] = useState([0, 1, 2, 3]);

  // Load flv.js dynamically
  useEffect(() => {
    const loadFlvJs = async () => {
      try {
        if (!flvjs) {
          const module = await import('flv.js');
          flvjs = module.default || module;
        }
        setFlvLoaded(true);
      } catch (err) {
        console.error('Failed to load flv.js:', err);
        setError('Error cargando reproductor de video');
      }
    };
    loadFlvJs();
  }, []);

  // Fetch stream URL and config from backend
  useEffect(() => {
    const fetchStreamAndConfig = async () => {
      if (!imei) {
        setError('IMEI no proporcionado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch video config for this specific device (audio control + allowed channels)
        try {
          const configResponse = await axios.get(`${BACKEND_URL}/api/video/config`, {
            params: { imei }
          });
          setAllowAudioControl(configResponse.data?.allow_audio_control || false);
          setAllowedChannels(configResponse.data?.allowed_channels || [0, 1, 2, 3]);
          
          // Check if requested channel is allowed
          const channels = configResponse.data?.allowed_channels || [0, 1, 2, 3];
          if (!channels.includes(channel)) {
            // Redirect to first allowed channel
            if (channels.length > 0) {
              window.location.href = `/live/${imei}?channel=${channels[0]}`;
              return;
            } else {
              setError('No hay canales habilitados para este dispositivo');
              setLoading(false);
              return;
            }
          }
        } catch (configErr) {
          console.warn('Could not fetch video config:', configErr);
          setAllowAudioControl(false);
          setAllowedChannels([0, 1, 2, 3]);
        }
        
        // Get stream URL
        const response = await axios.get(`${BACKEND_URL}/api/video/flv/${imei}`, {
          params: { channel }
        });
        
        if (response.data?.url) {
          setStreamUrl(response.data.url);
          setDeviceInfo(response.data.device || null);
        } else {
          setError('No se pudo obtener el stream de video');
        }
      } catch (err) {
        console.error('Error fetching stream:', err);
        const errorMsg = err.response?.data?.detail || 'Error al conectar con el servidor';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchStreamAndConfig();
  }, [imei, channel]);

  // Initialize FLV player when stream URL is available
  useEffect(() => {
    if (!streamUrl || !videoRef.current || !flvLoaded || !flvjs) return;

    // Check browser support
    if (!flvjs.isSupported()) {
      setError('Tu navegador no soporta reproducción FLV. Prueba con Chrome o Firefox.');
      return;
    }

    // Cleanup previous player
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload();
        playerRef.current.detachMediaElement();
        playerRef.current.destroy();
      } catch (e) {
        console.warn('Cleanup error:', e);
      }
      playerRef.current = null;
    }

    let player = null;
    
    try {
      player = flvjs.createPlayer({
        type: 'flv',
        url: streamUrl,
        isLive: true,
        hasAudio: true,
        hasVideo: true,
        cors: true,
      }, {
        enableWorker: false,
        enableStashBuffer: false,
        stashInitialSize: 128,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 3,
        autoCleanupMinBackwardDuration: 1,
      });

      player.attachMediaElement(videoRef.current);
      
      // Error handling - prevent crashes on undefined
      player.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
        console.error('FLV Error:', errorType, errorDetail, errorInfo);
        // Safely construct error message
        let message = 'Error de video';
        if (typeof errorDetail === 'string' && errorDetail) {
          message = errorDetail;
        } else if (typeof errorType === 'string' && errorType) {
          message = errorType;
        }
        setError(`Error de reproducción: ${message}`);
      });

      player.on(flvjs.Events.METADATA_ARRIVED, () => {
        setLoading(false);
      });

      player.on(flvjs.Events.LOADING_COMPLETE, () => {
        console.log('FLV loading complete');
      });

      // Load stream
      player.load();

      // Auto-play with proper error handling
      const video = videoRef.current;
      
      // Use video element play instead of player.play for better compatibility
      const attemptPlay = async () => {
        try {
          await video.play();
          setIsPlaying(true);
          setLoading(false);
        } catch (err) {
          console.warn('Autoplay blocked, waiting for user interaction:', err.name);
          setLoading(false);
          // Autoplay was blocked, user needs to click play
        }
      };
      
      // Wait a bit for stream to load before playing
      setTimeout(attemptPlay, 500);

      playerRef.current = player;

      // Video element events
      video.addEventListener('playing', () => {
        setIsPlaying(true);
        setLoading(false);
      });
      video.addEventListener('pause', () => setIsPlaying(false));
      video.addEventListener('error', (e) => {
        console.error('Video element error:', e);
        const videoError = video.error;
        if (videoError) {
          const errorMessages = {
            1: 'La carga del video fue abortada',
            2: 'Error de red al cargar el video',
            3: 'Error al decodificar el video',
            4: 'El formato de video no es soportado'
          };
          setError(errorMessages[videoError.code] || 'Error de video');
        }
      });

    } catch (err) {
      console.error('Player creation error:', err);
      setError(`Error al iniciar reproductor: ${err?.message || 'Error desconocido'}`);
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.unload();
          playerRef.current.detachMediaElement();
          playerRef.current.destroy();
        } catch (e) {
          console.warn('Cleanup error:', e);
        }
        playerRef.current = null;
      }
    };
  }, [streamUrl, flvLoaded]);

  const togglePlay = () => {
    if (!videoRef.current || !playerRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      playerRef.current.play();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-black flex flex-col"
    >
      {/* Header bar */}
      <div className="bg-gray-900 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="/logo192.png" 
            alt="4Sat" 
            className="h-8 w-8"
            onError={(e) => e.target.style.display = 'none'}
          />
          <div>
            <h1 className="text-white font-semibold text-sm">
              {deviceInfo?.name || `Dispositivo ${imei}`}
            </h1>
            <p className="text-gray-400 text-xs">
              Canal {channel + 1} • {isPlaying ? 'EN VIVO' : 'Conectando...'}
            </p>
          </div>
        </div>
        
        {isPlaying && (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-600 rounded text-white text-xs font-bold">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
        )}
      </div>

      {/* Video container */}
      <div className="flex-1 relative group">
        {/* Video element */}
        <video
          ref={videoRef}
          className="w-full h-full object-contain bg-black"
          muted={isMuted}
          playsInline
          autoPlay
        />

        {/* Loading overlay */}
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="flex flex-col items-center gap-3">
              <Circle size={48} className="animate-spin text-white" />
              <span className="text-white text-lg">Conectando stream...</span>
              <span className="text-gray-400 text-sm">IMEI: {imei}</span>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90">
            <div className="flex flex-col items-center gap-3 text-center p-6 max-w-md">
              <Warning size={64} className="text-red-500" />
              <h2 className="text-white text-xl font-semibold">Error de conexión</h2>
              <p className="text-gray-300">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Controls overlay - appears on hover */}
        {!loading && !error && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  title={isPlaying ? 'Pausar' : 'Reproducir'}
                >
                  {isPlaying ? (
                    <Pause size={24} className="text-white" weight="fill" />
                  ) : (
                    <Play size={24} className="text-white" weight="fill" />
                  )}
                </button>
                {/* Audio control - only visible if allowed by bridge config */}
                {allowAudioControl && (
                  <button
                    onClick={toggleMute}
                    className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    title={isMuted ? 'Activar audio' : 'Silenciar'}
                  >
                    {isMuted ? (
                      <SpeakerSlash size={24} className="text-white" />
                    ) : (
                      <SpeakerHigh size={24} className="text-white" />
                    )}
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {/* Channel selector - only show allowed channels */}
                <div className="flex gap-1">
                  {[0, 1, 2, 3].filter(ch => allowedChannels.includes(ch)).map((ch) => (
                    <a
                      key={ch}
                      href={`/live/${imei}?channel=${ch}`}
                      className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                        channel === ch
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                      CH{ch + 1}
                    </a>
                  ))}
                </div>
                
                <button
                  onClick={toggleFullscreen}
                  className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  title="Pantalla completa"
                >
                  <ArrowsOut size={24} className="text-white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-900 px-4 py-2 text-center">
        <p className="text-gray-500 text-xs">
          4Sat GPS Platform • Video en vivo
        </p>
      </div>
    </div>
  );
};

// Wrapped component with error boundary
const LiveVideoPageWrapper = () => (
  <VideoErrorBoundary>
    <LiveVideoPage />
  </VideoErrorBoundary>
);

export default LiveVideoPageWrapper;
